/**
 * DivisionScrollPicker - iOS-style scroll wheel for selecting scale readings
 *
 * Features:
 * - Smooth momentum scrolling with snap-to-division
 * - Centered on test load value initially
 * - Test load shown in blue as reference
 * - Quick action chips for common adjustments
 * - Option to switch to numeric keypad
 * - Haptic feedback on scroll snap
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Modal,
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  TextField,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Keyboard as KeyboardIcon,
  ViewList as ScrollIcon
} from '@mui/icons-material';
import {
  generateDivisionRange,
  formatToDivision,
  parseDivision,
  getDecimalPlaces,
  roundToDivision,
  triggerHaptic
} from '../../utils/divisionUtils';

const ITEM_HEIGHT = 48; // Height of each scroll item
const VISIBLE_ITEMS = 5; // Number of items visible at once

const DivisionScrollPicker = ({
  open,
  value,
  testLoad,
  division,
  capacity,
  unit = 'lb',
  onSelect,
  onClose
}) => {
  const theme = useTheme();
  const scrollRef = useRef(null);
  const [selectedValue, setSelectedValue] = useState(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [keypadValue, setKeypadValue] = useState('');
  const lastScrollIndex = useRef(-1);

  const divisionNum = parseDivision(division);
  const decimals = getDecimalPlaces(division);

  // Generate array of valid values centered on test load
  const validValues = useMemo(() =>
    generateDivisionRange(testLoad, division, capacity, { rangeMultiplier: 100 }),
    [testLoad, division, capacity]
  );

  // Find index for a value
  const getIndexForValue = useCallback((val) => {
    const numVal = parseFloat(val);
    return validValues.findIndex(v => Math.abs(v - numVal) < divisionNum / 2);
  }, [validValues, divisionNum]);

  // Initialize selected value and scroll position
  useEffect(() => {
    if (open) {
      const initialValue = (value !== '' && value !== null && value !== undefined)
        ? parseFloat(value)
        : testLoad;
      setSelectedValue(initialValue);
      setKeypadValue(formatToDivision(initialValue, division));
      lastScrollIndex.current = -1;

      // Scroll to initial position after render
      setTimeout(() => {
        if (scrollRef.current) {
          const index = getIndexForValue(initialValue);
          if (index >= 0) {
            scrollRef.current.scrollTop = index * ITEM_HEIGHT;
          }
        }
      }, 50);
    }
  }, [open, value, testLoad, division, getIndexForValue]);

  // Handle scroll with snap-to-division
  const handleScroll = useCallback((e) => {
    const scrollTop = e.target.scrollTop;
    const index = Math.round(scrollTop / ITEM_HEIGHT);
    const clampedIndex = Math.max(0, Math.min(index, validValues.length - 1));

    if (clampedIndex !== lastScrollIndex.current) {
      lastScrollIndex.current = clampedIndex;
      const snappedValue = validValues[clampedIndex];
      setSelectedValue(snappedValue);
      setKeypadValue(formatToDivision(snappedValue, division));
      triggerHaptic();
    }
  }, [validValues, division]);

  // Snap to nearest value on scroll end
  const handleScrollEnd = useCallback(() => {
    if (scrollRef.current && selectedValue !== null) {
      const index = getIndexForValue(selectedValue);
      if (index >= 0) {
        scrollRef.current.scrollTo({
          top: index * ITEM_HEIGHT,
          behavior: 'smooth'
        });
      }
    }
  }, [selectedValue, getIndexForValue]);

  // Handle quick action chip clicks
  const handleQuickAction = useCallback((newValue) => {
    setSelectedValue(newValue);
    setKeypadValue(formatToDivision(newValue, division));
    triggerHaptic();

    if (scrollRef.current) {
      const index = getIndexForValue(newValue);
      if (index >= 0) {
        scrollRef.current.scrollTo({
          top: index * ITEM_HEIGHT,
          behavior: 'smooth'
        });
      }
    }
  }, [division, getIndexForValue]);

  // Handle done button - MUST be defined before handleKeyDown which references it
  const handleDone = useCallback(() => {
    if (selectedValue !== null) {
      onSelect(formatToDivision(selectedValue, division));
    }
    onClose();
  }, [selectedValue, division, onSelect, onClose]);

  // Handle keyboard navigation for accessibility (WAI-ARIA listbox pattern)
  const handleKeyDown = useCallback((e) => {
    if (showKeypad) return; // Don't handle when keypad is active

    const currentIndex = selectedValue !== null ? getIndexForValue(selectedValue) : 0;
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        newIndex = Math.max(0, currentIndex - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        newIndex = Math.min(validValues.length - 1, currentIndex + 1);
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = validValues.length - 1;
        break;
      case 'PageUp':
        e.preventDefault();
        newIndex = Math.max(0, currentIndex - 5);
        break;
      case 'PageDown':
        e.preventDefault();
        newIndex = Math.min(validValues.length - 1, currentIndex + 5);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleDone();
        return;
      default:
        return;
    }

    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < validValues.length) {
      const newValue = validValues[newIndex];
      setSelectedValue(newValue);
      setKeypadValue(formatToDivision(newValue, division));
      triggerHaptic();

      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: newIndex * ITEM_HEIGHT,
          behavior: 'smooth'
        });
      }
    }
  }, [showKeypad, selectedValue, getIndexForValue, validValues, division, handleDone]);

  // Handle keypad input change
  const handleKeypadChange = (e) => {
    setKeypadValue(e.target.value);
  };

  // Handle keypad submit
  const handleKeypadSubmit = () => {
    const numValue = parseFloat(keypadValue);
    if (!isNaN(numValue)) {
      const rounded = roundToDivision(numValue, division);
      const clamped = Math.max(0, Math.min(capacity, rounded));
      setSelectedValue(clamped);
      setShowKeypad(false);

      if (scrollRef.current) {
        const index = getIndexForValue(clamped);
        if (index >= 0) {
          setTimeout(() => {
            scrollRef.current.scrollTo({
              top: index * ITEM_HEIGHT,
              behavior: 'smooth'
            });
          }, 50);
        }
      }
    }
  };

  // Format display value
  const formatDisplay = (val) => {
    return `${formatToDivision(val, division)} ${unit}`;
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <Box sx={{
        width: '100%',
        maxWidth: 400,
        bgcolor: 'background.paper',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        overflow: 'hidden',
        pb: 2
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Typography variant="subtitle1" fontWeight="bold">
            Select Reading
          </Typography>
          <Button onClick={handleDone} color="primary" variant="contained" size="small">
            Done
          </Button>
        </Box>

        {/* Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, py: 1 }}>
          <IconButton
            onClick={() => setShowKeypad(false)}
            color={!showKeypad ? 'primary' : 'default'}
            size="small"
          >
            <ScrollIcon />
          </IconButton>
          <IconButton
            onClick={() => setShowKeypad(true)}
            color={showKeypad ? 'primary' : 'default'}
            size="small"
          >
            <KeyboardIcon />
          </IconButton>
        </Box>

        {showKeypad ? (
          /* Numeric Keypad Mode */
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enter value (will round to nearest {formatToDivision(divisionNum, division)} {unit})
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={keypadValue}
              onChange={handleKeypadChange}
              onKeyDown={(e) => e.key === 'Enter' && handleKeypadSubmit()}
              inputProps={{
                inputMode: 'decimal',
                step: divisionNum,
                style: { fontSize: '1.5rem', textAlign: 'center' }
              }}
              autoFocus
            />
            <Button
              fullWidth
              variant="outlined"
              onClick={handleKeypadSubmit}
              sx={{ mt: 2 }}
            >
              Apply ({formatToDivision(roundToDivision(parseFloat(keypadValue) || 0, division), division)} {unit})
            </Button>
          </Box>
        ) : (
          /* Scroll Picker Mode */
          <>
            <Box sx={{
              position: 'relative',
              height: ITEM_HEIGHT * VISIBLE_ITEMS,
              overflow: 'hidden'
            }}>
              {/* Selection Indicator */}
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: 16,
                right: 16,
                height: ITEM_HEIGHT,
                transform: 'translateY(-50%)',
                bgcolor: theme.palette.action.selected,
                borderRadius: 1,
                pointerEvents: 'none',
                zIndex: 1
              }} />

              {/* Gradient overlays for fade effect */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: ITEM_HEIGHT * 2,
                background: `linear-gradient(to bottom, ${theme.palette.background.paper}, transparent)`,
                pointerEvents: 'none',
                zIndex: 2
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: ITEM_HEIGHT * 2,
                background: `linear-gradient(to top, ${theme.palette.background.paper}, transparent)`,
                pointerEvents: 'none',
                zIndex: 2
              }} />

              {/* Scrollable List */}
              <Box
                ref={scrollRef}
                onScroll={handleScroll}
                onTouchEnd={handleScrollEnd}
                onMouseUp={handleScrollEnd}
                onKeyDown={handleKeyDown}
                role="listbox"
                aria-label="Select scale reading value"
                aria-activedescendant={selectedValue !== null ? `picker-value-${selectedValue.toFixed(decimals)}` : undefined}
                tabIndex={0}
                sx={{
                  height: '100%',
                  overflowY: 'scroll',
                  WebkitOverflowScrolling: 'touch',
                  scrollSnapType: 'y mandatory',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                  // Padding to center first/last items
                  pt: `${ITEM_HEIGHT * Math.floor(VISIBLE_ITEMS / 2)}px`,
                  pb: `${ITEM_HEIGHT * Math.floor(VISIBLE_ITEMS / 2)}px`
                }}
              >
                {validValues.map((val, index) => {
                  const isSelected = selectedValue !== null &&
                    Math.abs(val - selectedValue) < divisionNum / 2;
                  const isTestLoad = Math.abs(val - testLoad) < divisionNum / 2;
                  const valueId = `picker-value-${val.toFixed(decimals)}`;

                  return (
                    <Box
                      key={`${val.toFixed(decimals)}-${index}`}
                      id={valueId}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleQuickAction(val)}
                      sx={{
                        height: ITEM_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        scrollSnapAlign: 'center',
                        fontSize: isSelected ? '1.25rem' : '1rem',
                        fontWeight: isSelected ? 'bold' : 'normal',
                        opacity: isSelected ? 1 : 0.5,
                        color: isTestLoad ? theme.palette.primary.main : 'inherit',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {formatDisplay(val)}
                      {isTestLoad && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 1, color: theme.palette.primary.main }}
                        >
                          (test load)
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Quick Action Chips */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              flexWrap: 'wrap',
              px: 2,
              mt: 2
            }}>
              <Chip
                label={`Exact (${formatToDivision(testLoad, division)})`}
                onClick={() => handleQuickAction(testLoad)}
                color={selectedValue !== null && Math.abs(selectedValue - testLoad) < divisionNum / 2 ? 'primary' : 'default'}
                size="small"
              />
              <Chip
                label={`-1 (${formatToDivision(testLoad - divisionNum, division)})`}
                onClick={() => handleQuickAction(Math.max(0, testLoad - divisionNum))}
                size="small"
              />
              <Chip
                label={`+1 (${formatToDivision(testLoad + divisionNum, division)})`}
                onClick={() => handleQuickAction(Math.min(capacity, testLoad + divisionNum))}
                size="small"
              />
              <Chip
                label={`-2 (${formatToDivision(testLoad - divisionNum * 2, division)})`}
                onClick={() => handleQuickAction(Math.max(0, testLoad - divisionNum * 2))}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`+2 (${formatToDivision(testLoad + divisionNum * 2, division)})`}
                onClick={() => handleQuickAction(Math.min(capacity, testLoad + divisionNum * 2))}
                size="small"
                variant="outlined"
              />
            </Box>
          </>
        )}

        {/* Current Selection Display */}
        <Box sx={{ textAlign: 'center', mt: 2, px: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Selected: <strong>{selectedValue !== null ? formatDisplay(selectedValue) : '-'}</strong>
          </Typography>
          {selectedValue !== null && Math.abs(selectedValue - testLoad) >= divisionNum / 2 && (
            <Typography variant="caption" color="text.secondary">
              ({selectedValue > testLoad ? '+' : ''}{formatToDivision(selectedValue - testLoad, division)} from test load)
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default DivisionScrollPicker;
